import { useMessageSource } from 'src/i18n/useMessageSource'
import { useGridTranslateHook } from 'src/shared/utils/hooks/grid-translate-hook'
import { Dispatch, useMemo } from 'react'
import { S } from 'src/shared/styled/S'
import { Chip, IconButton, Typography } from '@mui/material'
import { EditIcon } from 'src/shared/icons/Icons'
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro'
import { DataGridContainer } from 'src/shared/grid/DataGridContainer'
import { COLORS } from 'src/theme/colors'
import { ROUTES } from 'src/routing/routes'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import {
  DataGridDisabledStyled,
  DataGridInactiveRowCssClassId,
} from 'src/shared/styled/data-grid/DataGridDisabledStyled'
import {
  ConfigurationGridDataSettings,
  ConfigurationTablePageSortSettings,
} from 'src/shared/constants/table-settings-constants'
import { CONFIGURATION_STATUS } from 'src/shared/constants/constants'
import {
  getTranslationForMilestoneFrequency,
  getTranslationForMilestoneResponsibleType,
  getTranslationForMilestoneType,
  getTranslationForProcess,
  getTranslationForStatus,
} from 'src/screens/administration/configuration-management/ConfigurationUtils'

interface EvaluationCriteriaGridProps {
  dataSettings: ConfigurationGridDataSettings
  tableSettings: ConfigurationTablePageSortSettings
  setTableSettings: Dispatch<ConfigurationTablePageSortSettings>
}

export const MilestoneConfigurationGrid = (props: EvaluationCriteriaGridProps) => {
  const { getMessage } = useMessageSource()
  const gridTranslations = useGridTranslateHook()
  const navigate = useDelayedNavigate()

  const { rows, rowsCount, gridLoading } = props.dataSettings
  const { paginationModel, sortModel } = props.tableSettings

  const columns = useMemo(() => {
    return [
      {
        field: 'type',
        headerName: getMessage('label.type'),
        flex: 1,
        sortable: true,
        filterable: false,
        renderCell: (params: any) => {
          return getTranslationForMilestoneType(getMessage, params.value)
        },
      },
      {
        field: 'description',
        headerName: getMessage('label.description'),
        flex: 1.5,
        sortable: false,
        filterable: false,
      },
      {
        field: 'frequency',
        headerName: getMessage('label.milestone.config.frequency'),
        flex: 1,
        sortable: false,
        filterable: false,
        renderCell: (params: any) => {
          return getTranslationForMilestoneFrequency(getMessage, params.value)
        },
      },
      {
        field: 'due',
        headerName: getMessage('label.milestone.config.due'),
        flex: 1,
        sortable: false,
        filterable: false,
        renderCell: (params: any) => {
          return params.value ?? '-'
        },
      },
      {
        field: 'process',
        headerName: getMessage('label.process'),
        flex: 0.75,
        sortable: false,
        filterable: false,
        renderCell: (params: any) => {
          return params.value && <Chip label={getTranslationForProcess(getMessage, params.value)} />
        },
      },
      {
        field: 'responsible',
        headerName: getMessage('label.milestone.config.responsible'),
        flex: 1,
        sortable: false,
        filterable: false,
        renderCell: (params: any) => {
          return getTranslationForMilestoneResponsibleType(getMessage, params.value)
        },
      },
      {
        field: 'status',
        headerName: getMessage('label.status'),
        flex: 0.75,
        sortable: false,
        filterable: false,
        renderCell: (params: any) => {
          const statusKey = params.value ? CONFIGURATION_STATUS.ACTIVE : CONFIGURATION_STATUS.INACTIVE
          return <Chip label={getTranslationForStatus(getMessage, statusKey)} />
        },
      },
      {
        field: 'actions',
        headerName: getMessage('label.configuration.grid.actions'),
        headerAlign: 'right',
        align: 'right',
        flex: 0.5,
        resizable: false,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          return (
            <IconButton onClick={(e) => onEditClick(e, params.row.id)} color="primary">
              <EditIcon />
            </IconButton>
          )
        },
      },
    ] as GridColDef[]
  }, [getMessage, navigate])

  const onEditClick = (e: React.MouseEvent<HTMLButtonElement>, milestoneConfigId: string) => {
    e.stopPropagation()

    navigate(
      ROUTES.MilestonesConfigurationEdit.params({
        milestoneConfigId,
      }),
    )
  }

  return (
    <DataGridContainer>
      <DataGridPro
        rows={rows}
        rowCount={rowsCount}
        columns={columns}
        loading={gridLoading}
        localeText={gridTranslations}
        disableColumnPinning
        disableRowSelectionOnClick
        getRowClassName={(params) => (params.row.status ? '' : DataGridInactiveRowCssClassId)}
        disableColumnMenu
        pagination
        paginationModel={paginationModel}
        sortModel={sortModel}
        pageSizeOptions={[10, 25, 50]}
        filterMode="server"
        paginationMode="server"
        sortingMode="server"
        onPaginationModelChange={(newPagination) => {
          props.setTableSettings({
            ...props.tableSettings,
            paginationModel: newPagination,
          })
        }}
        onSortModelChange={(newSort) => {
          props.setTableSettings({
            ...props.tableSettings,
            sortModel: newSort,
          })
        }}
        sx={{
          boxShadow: 'none',
          border: 'none',
          ...DataGridDisabledStyled,
        }}
        slots={{
          toolbar: () => (
            <S.DataGrid.ToolbarContainer sx={{ backgroundColor: COLORS.grey }} style={{ paddingInline: '0' }}>
              <Typography variant="subtitle1">
                {`${getMessage('label.configuration.grid.toolbar.results')}: ${rowsCount}`}
              </Typography>
            </S.DataGrid.ToolbarContainer>
          ),
        }}
      />
    </DataGridContainer>
  )
}
